/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import ExportButton from './ExportButton';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';
import TableSkeleton from './states/TableSkeleton';
import './active-users.css';
import './CohortDetails.css';
import { formatUTCDate } from '../../utils/utils';

const PAGE_SIZE = 25;
const EXPORT_LIMIT = 100000;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isWeekly = (g) => g === 'week' || g === 'weekly';

const fmtDay = (d) => `${MON[d.getUTCMonth()]} ${d.getUTCDate()}`;

// Cohort label: monthly → 'YYYY-MM'; weekly → 'May 31 – Jun 6'.
const cohortLabel = (cohort, weekly) => {
  if (!weekly) return cohort;
  const start = new Date(`${cohort}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return cohort;
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return `${fmtDay(start)} – ${fmtDay(end)}`;
};

// Exact calendar range a given period covers — mirrors the backend activity window
// (cohortDetailsService: weekly → bucketStart + n weeks; monthly → cohort month + n months).
const periodRange = (cohort, period, weekly) => {
  if (weekly) {
    const start = new Date(`${cohort}T00:00:00Z`);
    if (Number.isNaN(start.getTime())) return '';
    start.setUTCDate(start.getUTCDate() + period * 7);
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    return `${fmtDay(start)} – ${fmtDay(end)}`;
  }
  const [y, m] = cohort.split('-').map(Number);
  if (!y || !m) return '';
  const d = new Date(Date.UTC(y, (m - 1) + period, 1));
  return `${MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const periodLabel = (period, weekly, cohort) => {
  if (period == null) return 'All registered users';
  const unit = weekly ? 'Week' : 'Month';
  const range = periodRange(cohort, period, weekly);
  const signup = period === 0 ? `signup ${weekly ? 'week' : 'month'}: ` : '';
  return `${unit} ${period} (${signup}${range})`;
};

const CohortDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const granularity = searchParams.get('granularity') || 'week';
  const cohort = searchParams.get('cohort') || '';
  const periodParam = searchParams.get('period'); // null → size mode
  const weekly = isWeekly(granularity);
  const period = periodParam == null || periodParam === '' ? null : Number(periodParam);

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('');   // '' | 'registered' | 'last_active'
  const [sortOrder, setSortOrder] = useState('DESC');

  useEffect(() => { refreshToken(); }, []);
  useEffect(() => { if (token) fetchDetails(1); }, [token]);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      navigate('/');
    }
  };

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    const now = new Date();
    if (expire * 1000 < now.getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  const windowStart = searchParams.get('window_start');
  const windowEndExcl = searchParams.get('window_end_excl');

  const baseParams = (sField = sortField, sOrder = sortOrder) => {
    const p = { granularity, cohort };
    if (period != null) p.period = period;
    if (windowStart) p.window_start = windowStart;
    if (windowEndExcl) p.window_end_excl = windowEndExcl;
    if (sField) { p.sort_field = sField; p.sort_order = sOrder; }
    return p;
  };

  const fetchDetails = async (pageNum, sField = sortField, sOrder = sortOrder) => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/most-active-cohorts/details`, {
        params: { ...baseParams(sField, sOrder), page: pageNum, limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data && res.data.data;
      setUsers((data && data.users) || []);
      setTotal((data && data.total) || 0);
      setPage(pageNum);
    } catch (err) {
      if (err.response && err.response.status === 401) { navigate('/'); return; }
      setError('We couldn’t load the users behind this cohort cell. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllForExport = async () => {
    const res = await axiosJWT.get(`${baseUrl}/admin/api/most-active-cohorts/details`, {
      params: { ...baseParams(), page: 1, limit: EXPORT_LIMIT },
      headers: { Authorization: `Bearer ${token}` },
    });
    return (res.data && res.data.data && res.data.data.users) || [];
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'DESC' ? 'ASC' : 'DESC';
    setSortField(field);
    setSortOrder(order);
    fetchDetails(1, field, order);
  };

  const sortIndicator = (field) => (sortField === field ? (sortOrder === 'DESC' ? ' ▼' : ' ▲') : '');

  const exportColumns = [
    { key: 'user_id', label: 'User ID' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'registered_date', label: 'Registered' },
    ...(period != null ? [
      { key: 'activity_count', label: 'App opens in period' },
      { key: 'last_active', label: 'Last active in period (UTC)' },
    ] : []),
  ];

  const exportName = `cohort-${cohort}${period != null ? `-period${period}` : '-all'}.csv`;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">
              People › <Link to="/unique-users-count">Most active users</Link> › <b>Cohort details</b>
            </span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/unique-users-count">← Back</Link></span>
          </div>

          <h1 className="au-title">Cohort details</h1>
          <p className="au-cohort-details__summary" data-testid="cohort-details-summary">
            Cohort <b>{cohortLabel(cohort, weekly)}</b> · {periodLabel(period, weekly, cohort)} ·{' '}
            <b>{Number(total).toLocaleString()}</b> user{total === 1 ? '' : 's'}
            {period != null
              ? ' who opened the app in this period'
              : ' who registered in this cohort'}
          </p>

          {loading ? (
            <div className="au-panel"><TableSkeleton rows={8} /></div>
          ) : error ? (
            <ErrorState message={error} onRetry={() => fetchDetails(page)} />
          ) : users.length === 0 ? (
            <EmptyState title="No users to show" message="This cohort cell has no users to list." />
          ) : (
            <div className="au-panel">
              <div className="au-panel__head">
                <h4 className="au-panel__title">Users</h4>
                <span className="au-spacer" />
                <ExportButton columns={exportColumns} fetchRows={fetchAllForExport} filename={exportName} />
              </div>
              <div className="au-cohort__scroll">
                <table className="au-cohort-details" data-testid="cohort-details-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th
                        className="au-cohort-details__sort"
                        data-testid="sort-registered"
                        onClick={() => handleSort('registered')}
                        role="button"
                        tabIndex={0}
                        title="Sort by registration date"
                      >
                        Registered{sortIndicator('registered')}
                      </th>
                      {period != null ? <th>App opens</th> : null}
                      {period != null ? (
                        <th
                          className="au-cohort-details__sort"
                          data-testid="sort-last_active"
                          onClick={() => handleSort('last_active')}
                          role="button"
                          tabIndex={0}
                          title="Sort by last active in this period"
                        >
                          Last active (UTC){sortIndicator('last_active')}
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u.user_id}>
                        <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td>{u.user_id}</td>
                        <td>{u.name || '-'}</td>
                        <td>{u.email || '-'}</td>
                        <td>{formatUTCDate(u.registered_date) || '-'}</td>
                        {period != null ? <td>{u.activity_count ?? '-'}</td> : null}
                        {period != null ? <td>{formatUTCDate(u.last_active) || '-'}</td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > PAGE_SIZE && (
                <Pagination
                  activePage={page}
                  itemsCountPerPage={PAGE_SIZE}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={fetchDetails}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CohortDetails;
